import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Card, 
  Icon, 
  SearchInput, 
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui';
import { toast } from 'sonner';
import { useCollections, useDeleteCollection } from '@/hooks/use-knowledge';

// =============================================================================
// Types
// =============================================================================

interface DeleteTarget {
  id: string;
  name: string;
}

function CollectionSourceTypeBadge({ sourceType }: { sourceType?: string | null }) {
  const { t } = useI18n();

  if (!sourceType) {
    return null;
  }

  const sourceConfig: Record<string, { className: string; icon: string; labelKey: string }> = {
    git: {
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      icon: 'git-branch',
      labelKey: 'admin.knowledge.collections.sourceTypes.git',
    },
    s3: {
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      icon: 'cloud',
      labelKey: 'admin.knowledge.collections.sourceTypes.s3',
    },
    sharepoint: {
      className: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
      icon: 'building-2',
      labelKey: 'admin.knowledge.collections.sourceTypes.sharepoint',
    },
    google_drive: {
      className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
      icon: 'hard-drive',
      labelKey: 'admin.knowledge.collections.sourceTypes.googleDrive',
    },
  };

  const config = sourceConfig[sourceType];
  if (!config) {
    return null;
  }

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      <Icon name={config.icon as never} size={12} />
      {t(config.labelKey)}
    </span>
  );
}

// =============================================================================
// Status Badge Component
// =============================================================================

function CollectionStatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const statusConfig: Record<string, { color: string; labelKey: string; animate?: boolean }> = {
    active: { color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', labelKey: 'admin.knowledge.collections.status.active' },
    processing: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', labelKey: 'admin.knowledge.collections.status.processing', animate: true },
    indexing: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', labelKey: 'admin.knowledge.collections.status.indexing', animate: true },
    error: { color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', labelKey: 'admin.knowledge.collections.status.error' },
    archived: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', labelKey: 'admin.knowledge.collections.status.archived' },
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${config.color}`}>
      {config.animate && <Icon name="loader" size={12} className="animate-spin" />}
      {t(config.labelKey)}
    </span>
  );
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function CollectionsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card key={i} className="p-4">
          <Skeleton className="h-10 w-10 rounded-lg mb-4" />
          <Skeleton className="h-5 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// Collections List Page
// =============================================================================

export function CollectionsListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Fetch collections from API
  const { data, isLoading, isError, error, refetch } = useCollections({
    page,
    page_size: 20,
    search: search || undefined,
  });

  const deleteCollection = useDeleteCollection();

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      await deleteCollection.mutateAsync(deleteTarget.id);
      toast.success(t('admin.knowledge.collections.deleteSuccess', { name: deleteTarget.name }));
      setDeleteTarget(null);
    } catch {
      toast.error(t('admin.knowledge.collections.deleteFailed'));
    }
  };

  // Handle search with debounce reset of page
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('admin.knowledge.collections.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.knowledge.collections.subtitle')}
          </p>
        </div>
        <Button leftIcon="plus" onClick={() => navigate('/knowledge/collections/new')}>
          {t('admin.knowledge.collections.newCollection')}
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-md">
        <SearchInput
          placeholder={t('admin.knowledge.collections.searchPlaceholder')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onClear={() => handleSearch('')}
        />
      </div>

      {/* Loading State */}
      {isLoading && <CollectionsSkeleton />}

      {/* Error State */}
      {isError && (
        <Card className="p-8 text-center">
          <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('admin.knowledge.collections.loadFailed')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {error instanceof Error ? error.message : t('common.error')}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !isError && data?.data.length === 0 && (
        <Card className="p-8 text-center">
          <Icon name="database" size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('admin.knowledge.collections.noCollections')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {search ? t('admin.knowledge.collections.searchHint') : t('admin.knowledge.collections.noCollectionsHint')}
          </p>
          {!search && (
            <Button leftIcon="plus" onClick={() => navigate('/knowledge/collections/new')}>
              {t('admin.knowledge.collections.newCollection')}
            </Button>
          )}
        </Card>
      )}

      {/* Collections Grid */}
      {!isLoading && !isError && data && data.data.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.data.map((col) => (
              <Card 
                key={col.id} 
                className="p-4 cursor-pointer hover:shadow-md transition-shadow relative group"
                onClick={() => navigate(`/knowledge/collections/${col.id}`)}
              >
                {/* Action Menu */}
                <div 
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Icon name="more-vertical" size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/knowledge/collections/${col.id}/edit`)}>
                        <Icon name="edit" size={14} className="mr-2" />
                        {t('common.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/knowledge/collections/${col.id}`)}>
                        <Icon name="file-text" size={14} className="mr-2" />
                        {t('common.viewDocuments')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600 focus:text-red-600"
                        onClick={() => setDeleteTarget({
                          id: col.id,
                          name: col.name,
                        })}
                      >
                        <Icon name="trash-2" size={14} className="mr-2" />
                        {t('common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900 w-fit mb-4">
                  <Icon name="database" size={24} className="text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{col.name}</h3>
                {col.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                    {col.description}
                  </p>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>{col.document_count.toLocaleString()} {t('admin.knowledge.collections.documents')}</span>
                  <span>•</span>
                  <span>{col.total_chunks.toLocaleString()} {t('common.chunks')}</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <CollectionStatusBadge status={col.status} />
                  {col.mode === 'full_document' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                      {t('admin.knowledge.collections.modeFullDocument')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {t('admin.knowledge.collections.modeRag')}
                    </span>
                  )}
                  <CollectionSourceTypeBadge sourceType={col.source_type} />
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {data.meta.total > data.meta.page_size && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                {t('common.previous')}
              </Button>
              <span className="text-sm text-gray-500 dark:text-gray-400 px-4">
                {t('common.pageOf', { page: data.meta.page, totalPages: data.meta.total_pages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.meta.total_pages}
                onClick={() => setPage(page + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.knowledge.collections.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.knowledge.collections.deleteDescription', { name: deleteTarget?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteCollection.isPending ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
